import { ref } from 'vue'

const allDone = ref(false)
const isOpen = ref(false)
const options = ref<{ id: string, status: ProgressStatus, label: string }[]>([])
const orphanedMedia = ref<OrphanedMediaOption[]>([])
const errorMessage = ref('')
let resolvePromise: ((value: boolean) => void) | null = null

enum ProgressStatus {
    NONE,
    UNSTARTED,
    STARTED,
    SUCCESS,
    ERROR
}

export type OrphanedMediaOption = {
    id: string
    type: 'image' | 'video' | 'file' | 'audio'
    bucket?: 'b-image' | 'b-video' | 'b-media' | 'b-file' | 'b-audio'
    label: string
    status: 'available' | 'deleting' | 'deleted' | 'error'
    deleteMedia: () => Promise<void>
}

export function useProgressPopup() {
    function start(opts: { id: string, status: ProgressStatus, label: string }[]): Promise<boolean> {
        options.value = opts
        orphanedMedia.value = []
        isOpen.value = true
        allDone.value = false
        errorMessage.value = ''
        return new Promise(resolve => {
            resolvePromise = resolve
        })
    }

    function updateOption(id: string, status: ProgressStatus) {
        options.value.forEach(option => {
            if (option.id === id) {
                option.status = status
            }
        })
        if (options.value.filter(item => item.status !== ProgressStatus.SUCCESS && item.status !== ProgressStatus.ERROR).length === 0) {
            allDone.value = true
        } else if (options.value.filter(item => item.status === ProgressStatus.ERROR).length > 0) {
            allDone.value = true
        }
    }

    function handleOk() {
        isOpen.value = false
        allDone.value = false
        errorMessage.value = ''
        resolvePromise?.(true)
    }

    function handleCancel() {
        isOpen.value = false
        allDone.value = false
        errorMessage.value = ''
        resolvePromise?.(false)
    }

    function setError(msg: string) {
        errorMessage.value = msg
    }

    function addOption(opt: { id: string, status: ProgressStatus, label: string }) {
        if (!options.value.find(o => o.id === opt.id)) {
            options.value.push(opt)
        }
    }

    function addOrphanedMedia(media: Omit<OrphanedMediaOption, 'status'>) {
        if (!orphanedMedia.value.some(item => item.id === media.id && item.type === media.type)) {
            orphanedMedia.value.push({ ...media, status: 'available' })
        }
    }

    async function deleteOrphanedMedia(media: OrphanedMediaOption) {
        media.status = 'deleting'
        try {
            await media.deleteMedia()
            media.status = 'deleted'
        } catch (error) {
            media.status = 'error'
            setError(error instanceof Error ? error.message : String(error))
        }
    }

    return {
        isOpen,
        options,
        errorMessage,
        start,
        handleOk,
        handleCancel,
        updateOption,
        ProgressStatus,
        allDone,
        setError,
        addOption,
        orphanedMedia,
        addOrphanedMedia,
        deleteOrphanedMedia,
    }
}
